import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import {
  BottomSheet,
  Option,
} from '@beeline/design-system-react/extracted-styles';
import { BottomSheetActionsType } from '@beeline/design-system-react/types/components/BottomSheet/BottomSheet.types';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { EmployeePermission } from 'src/api/permissions';
import { labelsByRoomType } from 'src/consts';
import { useHost } from 'src/providers/HostProvider';
import { getCurrentRoom, getRoomsState } from 'src/store/rooms/selectors';
import { roomsActions } from 'src/store/rooms/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { getUserPermissions } from 'src/store/user/selectors';
import { Room } from 'src/types';

import { RoomBookingForm } from '../RoomBookingSidesheet/RoomBookingForm';
import { RoomBookingSidesheetFields } from '../RoomBookingSidesheet/RoomBookingSidesheetFields';

import classes from './RoomBookingBottomSheet.module.scss';

interface FormBottomSheetProps {
  room?: Room;
  employeeNumber: string;
  permissions: Set<EmployeePermission>;
}

const Fields: FC<FormBottomSheetProps> = ({
  room,
  employeeNumber,
  permissions,
}) => {
  const { roomBookingSheetIsOpened } = useSelector(getRoomsState);

  const dispatch = useAppDispatch();
  const { submit, reset } = useForm();
  const title = useMemo((): string => {
    return room?.title && room.roomType
      ? `${labelsByRoomType[room.roomType]} ${room.title}`
      : 'Объект бронирования';
  }, [room?.roomType, room?.title]);

  const bookedFor = useField<Option<string> | null>('bookedFor').input.value;

  const handleSetOpen = useCallback(
    (value: boolean) => {
      dispatch(roomsActions.setRoomBookingSheetIsOpened(value));
      reset();
    },
    [dispatch, reset],
  );

  const book = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');

    handleSetOpen(false);
  }, [handleSetOpen, submit]);

  const actions = useMemo((): BottomSheetActionsType | undefined => {
    if (!permissions.has(EmployeePermission.BookRooms)) return undefined;

    return [
      {
        label:
          bookedFor && bookedFor.id !== employeeNumber
            ? 'Забронировать на коллегу'
            : 'Забронировать',
        variant: 'contained',
        onClick: book,
      },
    ];
  }, [book, bookedFor, employeeNumber, permissions]);

  return (
    <BottomSheet
      isOpen={roomBookingSheetIsOpened}
      handleSetOpen={handleSetOpen}
      swiper
      modal={false}
      snapPoints={[0.5, 1]}
      className={classes.settings}
      initialSnapPoint={1}
      header={{
        icon: Icons.Pin,
        title,
        closeButton: true,
      }}
      actions={actions}
    >
      <div className={classes.container}>
        <RoomBookingSidesheetFields room={room} permissions={permissions} />
      </div>
    </BottomSheet>
  );
};

export const RoomBookingBottomSheet: FC = () => {
  const { host } = useHost();
  const room = useSelector(getCurrentRoom);
  const data = useSelector(getUserPermissions);

  if (!data?.employeeNumber || !host) return null;

  return createPortal(
    <RoomBookingForm room={room}>
      <Fields
        room={room}
        employeeNumber={data.employeeNumber}
        permissions={data.permissions}
      />
    </RoomBookingForm>,
    host,
  );
};
